



















import { Component, Vue, Prop, Ref } from "vue-property-decorator";

interface Position {
  x: number;
  y: number;
}

const DEFAULT_MIN_SCALE = 0.5;
const DEFAULT_MAX_SCALE = 3;
const ZOOM_SENSITIVITY = 0.1;

@Component
export default class ZoomableContainer extends Vue {
  @Ref("container") readonly containerEl!: HTMLElement;
  @Prop({ default: DEFAULT_MIN_SCALE }) minScale!: number;
  @Prop({ default: DEFAULT_MAX_SCALE }) maxScale!: number;

  private scale = 1;
  private offset: Position = { x: 0, y: 0 };
  private isDragging = false;
  private lastTouchDistance = 0;
  private resizeObserver: ResizeObserver | null = null;
  private localMinScale = this.minScale;
  private lastDragPos: Position | null = null;

  get contentStyles() {
    return {
      transform: `translate(${this.offset.x}px, ${this.offset.y}px) scale(${
        this.scale
      })`,
      transformOrigin: "top left",
      transition: this.isDragging
        ? "none"
        : `transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)`,
      display: "inline-block"
    };
  }

  mounted() {
    this.initResizeObserver();
    this.calculateMinScale();
  }

  beforeDestroy() {
    this.cleanupResizeObserver();
  }

  initResizeObserver() {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.calculateMinScale);
      this.resizeObserver.observe(this.containerEl);
    } else {
      window.addEventListener("resize", this.calculateMinScale);
    }
  }

  cleanupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener("resize", this.calculateMinScale);
    }
  }

  calculateMinScale() {
    this.$nextTick(() => {
      const container = this.$refs.container as HTMLElement;
      const content = container.querySelector(
        ".zoom-content > *"
      ) as HTMLElement;

      if (!content) return;

      const containerWidth = container.clientWidth;

      // content.style.transform = "none";
      // content.style.width = "auto";
      // content.style.height = "auto";

      const contentWidth = content.offsetWidth;
      const contentHeight = content.offsetHeight;

      const scaleX = containerWidth / contentWidth;

      this.localMinScale = Math.min(scaleX, this.minScale);
      this.scale = this.localMinScale;
      this.offset = { x: 0, y: 0 };

      const scaledHeight = contentHeight * this.scale;
      container.style.height = `${scaledHeight}px`;
      container.style.minHeight = `${scaledHeight}px`;
    });
  }

  handleWheel(e: WheelEvent) {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = -Math.sign(e.deltaY) * ZOOM_SENSITIVITY;
      const newScale = Math.min(
        Math.max(this.scale + delta, this.localMinScale),
        this.maxScale
      );

      if (newScale === this.scale) {
        return;
      }

      const containerRect = this.containerEl.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const mouseY = e.clientY - containerRect.top;

      this.offset.x =
        mouseX - (mouseX - this.offset.x) * (newScale / this.scale);
      this.offset.y =
        mouseY - (mouseY - this.offset.y) * (newScale / this.scale);

      this.scale = newScale;
      this.constrainPosition();
    }
  }

  startDrag(e: MouseEvent) {
    if (e.button !== 0) return;
    this.isDragging = true;
    this.lastDragPos = { x: e.clientX, y: e.clientY };
    (this.$refs.container as HTMLElement).style.cursor = "grabbing";
  }

  onDrag(e: MouseEvent) {
    if (!this.isDragging || !this.lastDragPos) return;
    const dx = e.clientX - this.lastDragPos.x;
    const dy = e.clientY - this.lastDragPos.y;

    this.offset.x += dx;
    this.offset.y += dy;

    this.constrainPosition();
    this.lastDragPos = { x: e.clientX, y: e.clientY };
  }

  endDrag() {
    this.isDragging = false;
    this.lastDragPos = null;
    (this.$refs.container as HTMLElement).style.cursor = "grab";
  }

  handleTouchStart(e: TouchEvent) {
    if (e.touches.length === 2) {
      this.lastTouchDistance = this.getTouchDistance(e.touches);
    } else if (e.touches.length === 1) {
      this.isDragging = true;
      this.lastDragPos = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
  }

  handleTouchMove(e: TouchEvent) {
    if (e.touches.length === 2) {
      const currentDistance = this.getTouchDistance(e.touches);
      if (this.lastTouchDistance > 0) {
        const scaleFactor = currentDistance / this.lastTouchDistance;
        const newScale = Math.min(
          Math.max(this.scale * scaleFactor, this.localMinScale),
          this.maxScale
        );

        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        const containerRect = this.containerEl.getBoundingClientRect();
        const mouseX = centerX - containerRect.left;
        const mouseY = centerY - containerRect.top;

        this.offset.x =
          mouseX - (mouseX - this.offset.x) * (newScale / this.scale);
        this.offset.y =
          mouseY - (mouseY - this.offset.y) * (newScale / this.scale);
        this.scale = newScale;
        this.constrainPosition();
      }
      this.lastTouchDistance = currentDistance;
      e.preventDefault();
    } else if (this.isDragging && e.touches.length === 1 && this.lastDragPos) {
      if (this.scale <= this.localMinScale) {
        return;
      }
      const touch = e.touches[0];
      const dx = touch.clientX - this.lastDragPos.x;
      const dy = touch.clientY - this.lastDragPos.y;

      this.offset.x += dx;
      this.offset.y += dy;

      this.constrainPosition();
      this.lastDragPos = { x: touch.clientX, y: touch.clientY };
      e.preventDefault();
    }
  }

  // setScale(newScale: number) {
  //   newScale = Math.min(Math.max(newScale, this.localMinScale), this.maxScale);
  //   if (newScale !== this.scale) {
  //     this.scale = newScale;
  //     this.constrainPosition();
  //   }
  // }

  constrainPosition() {
    this.$nextTick(() => {
      const container = this.$refs.container as HTMLElement;
      const content = container.querySelector(
        ".zoom-content > *"
      ) as HTMLElement;
      if (!content) return;

      const contentWidth = content.offsetWidth * this.scale;
      const contentHeight = content.offsetHeight * this.scale;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const maxOffsetX = Math.max(0, contentWidth - containerWidth);
      const maxOffsetY = Math.max(0, contentHeight - containerHeight);

      if (contentWidth <= containerWidth) {
        this.offset.x = (containerWidth - contentWidth) / 2;
      } else {
        this.offset.x = Math.min(0, Math.max(-maxOffsetX, this.offset.x));
      }

      if (contentHeight <= containerHeight) {
        this.offset.y = (containerHeight - contentHeight) / 2;
      } else {
        this.offset.y = Math.min(0, Math.max(-maxOffsetY, this.offset.y));
      }
    });
  }

  getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
